// src/components/admin/ImportGapTab.tsx
// VIB-781: Knytt-først flyt + batch-import. Dialog returnerer LinkedDraft;
// parent samler opp og sender én batch til useMondayImport ved "Importer alle".
import { useMemo, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";
import { SrIcon } from "@/components/ui/SrIcon";
import { useMondayUsers, useMondayImport } from "@/hooks/use-monday-import";
import { useProjects } from "@/hooks/use-projects";
import {
  useMondayImportGap,
  type GapItem,
  type GroupCategory,
} from "@/hooks/use-monday-import-gap";
import {
  ImportGapItemDialog,
  type LinkedDraft,
} from "@/components/admin/ImportGapItemDialog";

const CURRENT_YEAR = new Date().getFullYear();
const YEAR_OPTIONS = [CURRENT_YEAR, CURRENT_YEAR - 1, CURRENT_YEAR - 2];

const CATEGORY_LABEL: Record<GroupCategory, string> = {
  project: "Prosjekt",
  intern: "Intern",
  tilbud: "Tilbud",
  pitch: "Pitch",
  treatment: "Treatment",
  ferie: "Ferie",
  avspasering: "Avspasering",
  fri: "Fri",
  timer_til_gode: "Timer til gode",
  other: "—",
};

function fmtHours(seconds: number): string {
  const h = seconds / 3600;
  return `${h.toFixed(1).replace(".", ",")}t`;
}

export function ImportGapTab() {
  const { data: users = [], isLoading: usersLoading } = useMondayUsers();
  const [userId, setUserId] = useState<string | null>(null);
  const [year, setYear] = useState<number>(CURRENT_YEAR);
  const [analyzed, setAnalyzed] = useState(false);
  const [expandedProjects, setExpandedProjects] = useState<Set<string>>(new Set());

  const gap = useMondayImportGap(userId, year, analyzed);
  const [dialogItem, setDialogItem] = useState<GapItem | null>(null);

  const [linked, setLinked] = useState<Map<number, LinkedDraft>>(new Map());
  const { data: projects = [] } = useProjects();
  const importMut = useMondayImport();

  const user = useMemo(() => users.find((u) => u.id === userId), [users, userId]);

  function toggleProject(key: string) {
    setExpandedProjects((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }

  function gapItemsForProject(code: string | null): GapItem[] {
    const all: GapItem[] = [];
    for (const g of gap.data?.groups ?? []) {
      for (const it of g.items) {
        if (!it.imported && (it.projectCode ?? null) === code) all.push(it);
      }
    }
    return all;
  }

  function saveLink(itemId: number, draft: LinkedDraft) {
    setLinked((prev) => {
      const next = new Map(prev);
      next.set(itemId, draft);
      return next;
    });
  }

  function unlink(itemId: number) {
    setLinked((prev) => {
      const next = new Map(prev);
      next.delete(itemId);
      return next;
    });
  }

  const allItems = useMemo(
    () => gap.data?.groups.flatMap((g) => g.items) ?? [],
    [gap.data],
  );

  const linkedItemsList = useMemo(
    () => allItems.filter((it) => linked.has(it.itemId)),
    [allItems, linked],
  );

  const linkedSecondsTotal = useMemo(
    () => linkedItemsList.reduce((sum, it) => sum + it.totalSeconds, 0),
    [linkedItemsList],
  );

  const projectNameById = useMemo(() => {
    const m = new Map<string, string>();
    for (const p of projects) m.set(p.id, p.name);
    return m;
  }, [projects]);

  async function importAll() {
    if (!user || linked.size === 0) return;
    const linkedGroupIds = Array.from(new Set(linkedItemsList.map((it) => it.groupId)));
    const overrides: Record<string, { skip?: boolean; project_id?: string | null; phase?: string | null; budget_line_id?: string | null; comment?: string | null }> = {};
    for (const it of allItems) {
      if (!linkedGroupIds.includes(it.groupId)) continue;
      const draft = linked.get(it.itemId);
      if (draft) {
        overrides[String(it.itemId)] = {
          project_id: draft.projectId,
          phase: draft.phase,
          budget_line_id: draft.budgetLineId,
          comment: draft.comment ?? undefined,
        };
      } else if (!it.imported) {
        overrides[String(it.itemId)] = { skip: true };
      }
    }
    await importMut.mutateAsync({
      userId: user.id,
      boardId: user.monday_board_id,
      groupIds: linkedGroupIds,
      dryRun: false,
      overrides,
    });
    setLinked(new Map());
    await gap.refetch();
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Import-gap</CardTitle>
          <p className="text-xs text-muted-foreground mt-1">
            Sammenligner alle Monday-items i valgte år mot{" "}
            <code className="text-[11px]">time_entries.monday_item_id</code>.
            Viser hva som mangler i Sølvrev per prosjekt.
          </p>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex flex-wrap items-end gap-3">
            <div className="space-y-1">
              <label className="text-xs text-muted-foreground">Ansatt</label>
              <Select
                value={userId ?? ""}
                onValueChange={(v) => {
                  setUserId(v);
                  setAnalyzed(false);
                }}
                disabled={usersLoading}
              >
                <SelectTrigger className="w-[220px] h-9">
                  <SelectValue placeholder="Velg ansatt" />
                </SelectTrigger>
                <SelectContent>
                  {users.map((u) => (
                    <SelectItem key={u.id} value={u.id}>
                      {u.full_name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-1">
              <label className="text-xs text-muted-foreground">År</label>
              <Select
                value={String(year)}
                onValueChange={(v) => {
                  setYear(Number(v));
                  setAnalyzed(false);
                }}
              >
                <SelectTrigger className="w-[110px] h-9">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {YEAR_OPTIONS.map((y) => (
                    <SelectItem key={y} value={String(y)}>
                      {y}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <Button
              onClick={() => setAnalyzed(true)}
              disabled={!userId || gap.isFetching}
            >
              {gap.isFetching ? "Analyserer…" : "Analyser"}
            </Button>
          </div>

          {gap.isError && (
            <div className="text-sm text-red-600">
              {(gap.error as Error).message}
            </div>
          )}

          {gap.data && (
            <div className="space-y-4">
              <SummaryCard summary={gap.data.summary} />

              {linked.size > 0 && (
                <div className="flex items-center justify-between gap-3 p-3 rounded-lg border border-emerald-200 bg-emerald-50 dark:bg-emerald-950/30">
                  <div className="text-sm">
                    <span className="font-medium">{linked.size} knyttede items</span>
                    <span className="text-muted-foreground"> · {(linkedSecondsTotal / 3600).toFixed(1).replace(".", ",")}t totalt</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setLinked(new Map())}
                      disabled={importMut.isPending}
                    >
                      Tøm alle
                    </Button>
                    <Button
                      size="sm"
                      onClick={importAll}
                      disabled={importMut.isPending}
                    >
                      {importMut.isPending ? "Importerer…" : `Importer alle (${linked.size})`}
                    </Button>
                  </div>
                </div>
              )}

              <div className="rounded-lg border border-border/50 overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-muted/30">
                    <tr className="text-xs text-muted-foreground">
                      <th className="text-left p-3 font-medium">Prosjekt</th>
                      <th className="text-right p-3 font-medium">Items</th>
                      <th className="text-right p-3 font-medium">Importert</th>
                      <th className="text-right p-3 font-medium">Gap</th>
                      <th className="text-right p-3 font-medium">Timer i gap</th>
                      <th className="w-8" />
                    </tr>
                  </thead>
                  <tbody>
                    {gap.data.projectAggregate.map((p) => {
                      const key = p.projectCode ?? "_null";
                      const expanded = expandedProjects.has(key);
                      const items = expanded ? gapItemsForProject(p.projectCode) : [];
                      return (
                        <>
                          <tr
                            key={key}
                            onClick={() => toggleProject(key)}
                            className="border-t border-border/50 cursor-pointer hover:bg-muted/30 transition-colors"
                          >
                            <td className="p-3">
                              <div className="font-medium">
                                {p.projectName ?? p.projectCode ?? "(uten prosjekt-kode)"}
                              </div>
                              {p.projectCode && (
                                <div className="text-xs text-muted-foreground">
                                  Kode {p.projectCode}
                                </div>
                              )}
                            </td>
                            <td className="p-3 text-right tabular-nums">{p.totalItems}</td>
                            <td className="p-3 text-right tabular-nums text-emerald-600">
                              {p.importedItems}
                            </td>
                            <td
                              className={cn(
                                "p-3 text-right tabular-nums",
                                p.gapItems > 0 ? "text-red-600 font-medium" : "text-muted-foreground",
                              )}
                            >
                              {p.gapItems}
                            </td>
                            <td className="p-3 text-right tabular-nums">
                              {fmtHours(p.totalSecondsInGap)}
                            </td>
                            <td className="p-3 text-muted-foreground">
                              <SrIcon
                                name={expanded ? "chevron-down" : "chevron-right"}
                                className="w-4 h-4"
                              />
                            </td>
                          </tr>
                          {expanded && items.length > 0 && (
                            <tr key={`${key}-items`} className="bg-muted/10">
                              <td colSpan={6} className="p-3">
                                <ul className="space-y-1.5">
                                  {items.map((it) => (
                                    <li
                                      key={it.itemId}
                                      className="flex items-center justify-between gap-3 text-xs"
                                    >
                                      <div className="min-w-0 flex-1">
                                        <div className="font-medium truncate">{it.name}</div>
                                        <div className="text-muted-foreground">
                                          {it.groupTitle} ·{" "}
                                          <Badge variant="secondary" className="text-[10px]">
                                            {CATEGORY_LABEL[it.groupCategory]}
                                          </Badge>{" "}
                                          · {it.sessionsCount} sessions
                                          {(it.missingSessionsCount ?? 0) > 0 && (
                                            <>
                                              {" · "}
                                              <span className="text-destructive font-medium">
                                                {it.missingSessionsCount} mangler
                                                {it.missingSeconds
                                                  ? ` (${fmtHours(it.missingSeconds)})`
                                                  : ""}
                                              </span>
                                            </>
                                          )}
                                        </div>
                                      </div>
                                      <div className="text-right tabular-nums whitespace-nowrap">
                                        {fmtHours(it.totalSeconds)}
                                      </div>
                                      <div className="flex items-center gap-2">
                                        {linked.has(it.itemId) && (
                                          <Badge variant="secondary" className="text-[10px] flex items-center gap-1">
                                            → {projectNameById.get(linked.get(it.itemId)!.projectId) ?? "Knyttet"}
                                            <button
                                              type="button"
                                              onClick={(e) => {
                                                e.stopPropagation();
                                                unlink(it.itemId);
                                              }}
                                              className="-mr-1 hover:text-red-600"
                                              aria-label="Fjern tilknytning"
                                            >
                                              ×
                                            </button>
                                          </Badge>
                                        )}
                                        <Button
                                          size="sm"
                                          variant="outline"
                                          onClick={() => setDialogItem(it)}
                                        >
                                          {linked.has(it.itemId) ? "Endre" : "Knytt"}
                                        </Button>
                                      </div>
                                    </li>
                                  ))}
                                </ul>
                              </td>
                            </tr>
                          )}
                        </>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {dialogItem && (
        <ImportGapItemDialog
          open={!!dialogItem}
          onOpenChange={(open) => !open && setDialogItem(null)}
          item={dialogItem}
          initialDraft={linked.get(dialogItem.itemId)}
          onConfirm={(itemId, draft) => saveLink(itemId, draft)}
        />
      )}
    </div>
  );
}

function SummaryCard({
  summary,
}: {
  summary: { totalItems: number; importedItems: number; gapItems: number; totalSecondsInGap: number };
}) {
  return (
    <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
      <SummaryStat label="Items totalt" value={String(summary.totalItems)} />
      <SummaryStat
        label="Importert"
        value={String(summary.importedItems)}
        tone="success"
      />
      <SummaryStat
        label="Gap"
        value={String(summary.gapItems)}
        tone={summary.gapItems > 0 ? "danger" : "neutral"}
      />
      <SummaryStat
        label="Timer i gap"
        value={fmtHours(summary.totalSecondsInGap)}
        tone={summary.gapItems > 0 ? "danger" : "neutral"}
      />
    </div>
  );
}

function SummaryStat({
  label,
  value,
  tone = "neutral",
}: {
  label: string;
  value: string;
  tone?: "success" | "danger" | "neutral";
}) {
  const toneClass =
    tone === "success"
      ? "text-emerald-600"
      : tone === "danger"
      ? "text-red-600"
      : "text-foreground";
  return (
    <div className="p-3 rounded-lg border border-border/50 bg-card/50">
      <div className="text-xs text-muted-foreground">{label}</div>
      <div className={cn("text-2xl font-bold tabular-nums mt-1", toneClass)}>
        {value}
      </div>
    </div>
  );
}
